import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
  archiveAccount,
  createAccount,
  deleteTransaction,
  bookWithoutReceipt,
  createManualTransaction,
  deleteBookingRule,
  linkDocument,
  listBookingRules,
  listAccounts,
  listJournalLines,
  listTransactions,
  saveBookingRule,
  saveImportPreset,
  unlinkDocument,
  updateAccount,
  type AccountInput,
  type BookingInput,
  type BookingRuleRecord,
  type ManualEntry,
  type TransactionFilters,
} from "@/lib/api/banking";
import {
  commitImport,
  listImports,
  resolveDuplicate,
  undoImport,
  type ImportFile,
  type StatementImport,
} from "@/lib/api/statements";
import type { Transaction } from "@/lib/api/banking";
import type { DocumentRecord } from "@/lib/api/documents";
import { transferMirror } from "@/lib/banking/money";
import { autoLinkPlan } from "@/lib/banking/period-close";
import { ruleBookings, type BookingRule } from "@/lib/banking/rules";
import type { ImportPlan } from "@/lib/import/reconcile";
import type { ColumnMapping } from "@/lib/import/transactions";

export const bankingKeys = {
  all: ["banking"] as const,
  accounts: (workspaceId: string) => [...bankingKeys.all, "accounts", workspaceId] as const,
  transactions: (workspaceId: string, filters: TransactionFilters) =>
    [...bankingKeys.all, "transactions", workspaceId, filters] as const,
  imports: (workspaceId: string) => [...bankingKeys.all, "imports", workspaceId] as const,
};

export function useAccounts(workspaceId: string | undefined) {
  return useQuery({
    queryKey: bankingKeys.accounts(workspaceId ?? ""),
    queryFn: () => listAccounts(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

export function useTransactions(workspaceId: string | undefined, filters: TransactionFilters = {}) {
  return useQuery({
    queryKey: bankingKeys.transactions(workspaceId ?? "", filters),
    queryFn: () => listTransactions(workspaceId!, filters),
    enabled: Boolean(workspaceId),
  });
}

function useInvalidateBanking() {
  const queryClient = useQueryClient();
  return async () => {
    // A list whose first fetch is still running (an account just selected) would otherwise get
    // that older answer back from invalidateQueries.
    await queryClient.cancelQueries({ queryKey: bankingKeys.all });
    void queryClient.invalidateQueries({ queryKey: bankingKeys.all });
    void queryClient.invalidateQueries({ queryKey: ["documents"] });
  };
}

export function useCreateAccount(workspaceId: string) {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: (input: AccountInput) => createAccount(workspaceId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateAccount() {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<AccountInput> }) =>
      updateAccount(id, input),
    onSuccess: invalidate,
  });
}

export function useArchiveAccount() {
  const invalidate = useInvalidateBanking();
  return useMutation({ mutationFn: archiveAccount, onSuccess: invalidate });
}

export function useImports(workspaceId: string | undefined) {
  return useQuery({
    queryKey: bankingKeys.imports(workspaceId ?? ""),
    queryFn: () => listImports(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

export function useCommitImport(workspaceId: string, userId: string) {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: async (args: {
      accountId: string;
      file: ImportFile;
      plan: ImportPlan;
      records: Map<string, Transaction>;
      /** Column mapping of a CSV/XLSX import, remembered for the next statement of this account. */
      preset?: ColumnMapping;
    }) => {
      const record = await commitImport({ workspaceId, userId, ...args });
      if (args.preset) await saveImportPreset(args.accountId, args.preset);
      return record;
    },
    onSuccess: invalidate,
  });
}

export function useUndoImport() {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: (record: StatementImport) => undoImport(record),
    onSuccess: invalidate,
  });
}

export function useResolveDuplicate() {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "merge" | "keep" }) =>
      resolveDuplicate(id, decision),
    onSuccess: invalidate,
  });
}

export function useLinkDocument() {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: ({ transactionId, documentId }: { transactionId: string; documentId: string }) =>
      linkDocument(transactionId, documentId),
    onSuccess: invalidate,
  });
}

export function useUnlinkDocument() {
  const invalidate = useInvalidateBanking();
  return useMutation({ mutationFn: unlinkDocument, onSuccess: invalidate });
}

export function useBookWithoutReceipt() {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: (args: {
      transaction: Transaction;
      input: BookingInput;
      mirror?: Transaction | null;
    }) => bookWithoutReceipt(args.transaction, args.input, args.mirror),
    onSuccess: invalidate,
  });
}

export function useCreateManualTransaction(workspaceId: string) {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: ({ accountId, entry }: { accountId: string; entry: ManualEntry }) =>
      createManualTransaction(workspaceId, accountId, entry),
    onSuccess: invalidate,
  });
}

export function useBookingRules(workspaceId: string | undefined) {
  return useQuery({
    queryKey: [...bankingKeys.all, "rules", workspaceId ?? ""],
    queryFn: () => listBookingRules(workspaceId!),
    enabled: Boolean(workspaceId),
  });
}

export function useSaveBookingRule() {
  const invalidate = useInvalidateBanking();
  return useMutation({
    mutationFn: (args: { transaction: Transaction; input: BookingInput }) =>
      saveBookingRule(args.transaction, args.input),
    onSuccess: invalidate,
  });
}

export function useDeleteBookingRule() {
  const invalidate = useInvalidateBanking();
  return useMutation({ mutationFn: deleteBookingRule, onSuccess: invalidate });
}

/**
 * Books the open rows a rule covers (`ruleBookings`) whenever the data shows new ones, like
 * `useAutoLink`: each row is tried once per page visit. A transfer finds its other half the way a
 * person's booking does. Calls `onBooked` with the number of rows it booked.
 */
export function useAutoBook(
  transactions: Transaction[] | undefined,
  rules: BookingRuleRecord[] | undefined,
  enabled: boolean,
  onBooked: (count: number) => void,
) {
  const queryClient = useQueryClient();
  const tried = useRef(new Set<string>());
  const report = useRef(onBooked);
  useEffect(() => {
    report.current = onBooked;
  });

  useEffect(() => {
    if (!enabled || !transactions || !rules || rules.length === 0) return;
    const due = ruleBookings(transactions, rules as BookingRule[]).filter(
      ({ transaction }) => !tried.current.has(transaction.id),
    );
    if (due.length === 0) return;
    for (const { transaction } of due) tried.current.add(transaction.id);
    void (async () => {
      let booked = 0;
      // One after the other: two transfers must not claim the same other half.
      for (const { transaction, rule } of due) {
        const mirror =
          rule.bookingType === "transfer" && rule.transferAccount
            ? transferMirror(transaction, rule.transferAccount, transactions)
            : null;
        try {
          await bookWithoutReceipt(transaction, rule, mirror, "auto");
          booked += 1;
        } catch {
          // Tried once; the row stays open for a person.
        }
      }
      if (booked === 0) return;
      report.current(booked);
      await queryClient.cancelQueries({ queryKey: bankingKeys.all });
      void queryClient.invalidateQueries({ queryKey: bankingKeys.all });
    })();
  }, [transactions, rules, enabled, queryClient]);
}

/** The journal on one Sachkonto (the cash book); refetched with everything else on the page. */
export function useJournalLines(workspaceId: string | undefined, account: string | undefined) {
  return useQuery({
    queryKey: [...bankingKeys.all, "journal", workspaceId ?? "", account ?? ""],
    queryFn: () => listJournalLines(workspaceId!, account!),
    enabled: Boolean(workspaceId && account),
  });
}

export function useDeleteTransaction() {
  const invalidate = useInvalidateBanking();
  return useMutation({ mutationFn: deleteTransaction, onSuccess: invalidate });
}

/**
 * Links the unambiguous charge/receipt pairs (`autoLinkPlan`) whenever the data shows new ones -
 * after an import, after a receipt arrived by mail. Each pair is tried once per page visit, so a
 * failing write cannot loop. Calls `onLinked` with the number of pairs it linked.
 */
export function useAutoLink(
  transactions: Transaction[] | undefined,
  documents: DocumentRecord[] | undefined,
  enabled: boolean,
  onLinked: (count: number) => void,
) {
  const queryClient = useQueryClient();
  const tried = useRef(new Set<string>());
  const report = useRef(onLinked);
  useEffect(() => {
    report.current = onLinked;
  });

  useEffect(() => {
    if (!enabled || !transactions || !documents) return;
    const pairs = autoLinkPlan(transactions, documents).filter(
      (pair) => !tried.current.has(pair.transactionId),
    );
    if (pairs.length === 0) return;
    for (const pair of pairs) tried.current.add(pair.transactionId);
    void Promise.allSettled(
      pairs.map((pair) => linkDocument(pair.transactionId, pair.documentId, "auto")),
    ).then((results) => {
      const linked = results.filter((result) => result.status === "fulfilled").length;
      if (linked === 0) return;
      report.current(linked);
      void queryClient.invalidateQueries({ queryKey: bankingKeys.all });
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    });
  }, [transactions, documents, enabled, queryClient]);
}
